import React, { useRef, useEffect } from "react";
import { Box } from "@mui/material";

export interface BoxParams {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

export interface EnvironmentLinkProps {
  start: BoxParams;
  end: BoxParams;
  highlighted: boolean;
  color?: string;
  thickness?: number;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onClick?: () => void;
}

const cm = 5; // Margin around the cursor to check for proximity to the line

export const EnvironmentLink = ({
  start,
  end,
  highlighted,
  color = "#1976d2",
  onMouseOver,
  onMouseOut,
  onClick,
}: EnvironmentLinkProps) => {
  const boxRef = useRef<HTMLDivElement>(null);

  // Calculate the center points of start and end boxes
  const startX = start.x + (start.width || 0) / 2;
  const startY = start.y + (start.height || 0) / 2;
  const endX = end.x + (end.width || 0) / 2;
  const endY = end.y + (end.height || 0) / 2;

  // Calculate the distance from a point to the line
  const distanceFromPointToLine = (
    pointX: number,
    pointY: number,
    lineStartX: number,
    lineStartY: number,
    lineEndX: number,
    lineEndY: number,
  ): number => {
    if (lineEndX > lineStartX && (pointX < lineStartX - cm || pointX > lineEndX + cm)) return Number.POSITIVE_INFINITY;
    if (lineEndX < lineStartX && (pointX > lineStartX + cm || pointX < lineEndX - cm)) return Number.POSITIVE_INFINITY;
    if (lineEndY > lineStartY && (pointY < lineStartY - cm || pointY > lineEndY + cm)) return Number.POSITIVE_INFINITY;
    if (lineEndY < lineStartY && (pointY > lineStartY + cm || pointY < lineEndY - cm)) return Number.POSITIVE_INFINITY;

    const lineLength = Math.sqrt(Math.pow(lineEndX - lineStartX, 2) + Math.pow(lineEndY - lineStartY, 2));
    if (lineLength === 0) return 0;

    const distance =
      Math.abs(
        (lineEndY - lineStartY) * pointX - (lineEndX - lineStartX) * pointY + lineEndX * lineStartY - lineEndY * lineStartX,
      ) / lineLength;

    return distance;
  };

  // Handle document mouse movement
  const handleDocumentMouseMove = (e: MouseEvent) => {
    if (!boxRef.current) return;

    // Calculate cursor position relative to the SVG
    const rect = boxRef.current.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    // Only process if cursor is within or near the component bounds
    if (cursorX >= -cm && cursorX <= rect.width + cm && cursorY >= -cm && cursorY <= rect.height + cm) {
      const distance = distanceFromPointToLine(cursorX, cursorY, startX, startY, endX, endY);

      // Check if cursor is close enough to the line (within 5 pixels)
      if (distance <= cm && onMouseOver) {
        onMouseOver();
        return;
      }
    }

    if (highlighted && onMouseOut) {
      onMouseOut();
    }
  };

  const handleDocumentClick = () => {
    if (highlighted && onClick) {
      onClick();
    }
  };

  // We use listeners on the document to handle mouse events because we want to pinpoint the exact location of the cursor when it is over the line
  useEffect(() => {
    document.addEventListener("mouseup", handleDocumentClick);
    document.addEventListener("mousemove", handleDocumentMouseMove);

    return () => {
      document.removeEventListener("mouseup", handleDocumentClick);
      document.removeEventListener("mousemove", handleDocumentMouseMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startX, startY, endX, endY, highlighted]);

  return (
    <Box
      ref={boxRef}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        cursor: highlighted ? "pointer" : "default",
      }}
    >
      <svg width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0 }}>
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke={color}
          strokeWidth={highlighted ? 6 : 2}
          strokeOpacity={highlighted ? 1 : 0.7}
        />
      </svg>
    </Box>
  );
};
